import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notification } from "antd";
import { storage } from "utils/storage";
import { useNavigate } from "react-router-dom";
import { RecruiterLoginPayloadInterface } from "./dto";
import { signInWithEmailAndPassword, fetchSignInMethodsForEmail } from "firebase/auth";
import { auth, db } from "../../../firebase/firebase";
import { query, where, collection, getDocs } from "firebase/firestore";
import { RoleTypes } from "enum/role-type-enum";

export const loginRecruiter = async (
  data: RecruiterLoginPayloadInterface
): Promise<{ data: any; token: string } | null> => {
  try {
    console.log("🔐 Login attempt with:", data);

    const userCredential = await signInWithEmailAndPassword(
      auth,
      data.emailOrPhone,
      data.password
    );
    const user = userCredential.user;
    const token = await user.getIdToken();

    const userInfoQuery = query(
      collection(db, "user_infos"),
      where("uid", "==", user.uid)
    );

    const userDocSnap = await getDocs(userInfoQuery);
    if (!userDocSnap.empty) {
      const existingUserInfo = userDocSnap.docs[0].data();
      if (!existingUserInfo.role || existingUserInfo.role !== RoleTypes.RECRUITER) {
        throw new Error(
          `Login Error: Your registered role (${existingUserInfo.role}) does not match the login method.`
        );
      }

      storage.setUserInStorage(existingUserInfo);
      storage.setTokenInStorage(token);
      return { data: existingUserInfo, token };
    } else {
      throw new Error("Login Error: User not found.");
    }

  } catch (error: any) {
    console.error("❌ Login failed:", error);

    if (error.code === "auth/user-not-found" || error.code === "auth/wrong-password") {
      const signInMethods = await fetchSignInMethodsForEmail(auth, data.emailOrPhone);
      if (signInMethods.includes("google.com")) {
        return Promise.reject(new Error("This email is registered with Google. Please login using Google."));
      }
    }

    return Promise.reject(new Error("Invalid Email or password."));
  }
};

export const useLoginRecruiter = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: loginRecruiter,
    onSuccess: async (response) => {
      if (response) {
        const { data, token } = response;
        storage.setUserInStorage(data);
        storage.setTokenInStorage(token);
        await queryClient.invalidateQueries({ queryKey: ["recruiter"] });
        notification.success({
          message: "Logged In",
          description: "You have successfully logged in.",
        });
        navigate("/recruiter/home");
      }
    },
    onError: (error: any) => {
      notification.error({
        message: "Login Failed",
        description: error?.message || "An error occurred during login.",
      });
    },
  });
};
